/**
 * Analytics periods — the single definition of "which instants does a period cover" and "what is
 * it compared against". Pure (no clock, no I/O): the caller passes `now` and the tenant time zone.
 *
 * A period is a range of local calendar days [from, to] (inclusive) mapped to the half-open
 * instant range [start, end). When the range reaches into the future (today, this month, ...) the
 * end is `now` and the period is `partial`; its comparison then covers the SAME elapsed time from
 * the start of the comparison window (today until 15:00 vs yesterday until 15:00), never a whole
 * finished period against an unfinished one.
 */

export const PERIOD_PRESETS = ['today', 'yesterday', 'last7', 'last30', 'this_month', 'last_month', 'this_year', 'custom'] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

export const COMPARISON_MODES = ['previous', 'year', 'none'] as const;
export type ComparisonMode = (typeof COMPARISON_MODES)[number];

export const GRANULARITIES = ['hour', 'day', 'week', 'month'] as const;
export type Granularity = (typeof GRANULARITIES)[number];

/** Longest custom range (days). Longer ranges belong to exports, not to an interactive dashboard. */
export const MAX_PERIOD_DAYS = 400;

export interface PeriodWindow {
  /** First local day, YYYY-MM-DD. */
  from: string;
  /** Last local day (inclusive), YYYY-MM-DD. */
  to: string;
  /** Inclusive start instant (ISO). */
  start: string;
  /** Exclusive end instant (ISO): end of `to`, or `now` for a partial window. */
  end: string;
}

export interface ResolvedPeriod {
  preset: PeriodPreset;
  label: string;
  current: PeriodWindow;
  previous: PeriodWindow | null;
  compare: ComparisonMode;
  compareLabel: string | null;
  /** The current window ends at `now` (the period is not over yet). */
  partial: boolean;
  /** Calendar days in the current window. */
  days: number;
  granularity: Granularity;
  timezone: string;
}

export interface PeriodInput {
  preset: PeriodPreset;
  from?: string | null;
  to?: string | null;
  compare?: ComparisonMode;
  granularity?: Granularity | null;
}

export class PeriodError extends Error {
  readonly code = 'INVALID_PERIOD';
}

// ------------------------------------------------------------------ civil date helpers (UTC based, no TZ)
const DAY_MS = 86_400_000;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDate(s: string): number {
  const m = DATE_RE.exec(s);
  if (!m) throw new PeriodError(`Fecha no válida: ${s}`);
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (fmtDate(t) !== s) throw new PeriodError(`Fecha no válida: ${s}`);
  return t;
}
const fmtDate = (t: number) => new Date(t).toISOString().slice(0, 10);
export const addDays = (d: string, n: number) => fmtDate(parseDate(d) + n * DAY_MS);
export const daysBetween = (a: string, b: string) => Math.round((parseDate(b) - parseDate(a)) / DAY_MS);

function ymd(d: string) {
  const t = new Date(parseDate(d));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth(), d: t.getUTCDate() };
}
/** Day `day` of month `m` (0-based, may overflow into other years) clamped to the month length (31 Mar - 1 month = 28/29 Feb). */
function clampedDate(y: number, m: number, day: number): string {
  const first = Date.UTC(y, m, 1);
  const len = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return fmtDate(first + (Math.min(day, len) - 1) * DAY_MS);
}
const monthStart = (d: string, offset = 0) => {
  const { y, m } = ymd(d);
  return clampedDate(y, m + offset, 1);
};
const monthEnd = (d: string, offset = 0) => {
  const { y, m } = ymd(d);
  return clampedDate(y, m + offset, 31);
};
const shiftYears = (d: string, n: number) => {
  const x = ymd(d);
  return clampedDate(x.y + n, x.m, x.d);
};

// ------------------------------------------------------------------ time zone helpers
/** Local calendar day of an instant in a time zone. */
export function localDate(at: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);
}

/** Offset (ms) of the time zone at an instant: local wall clock minus UTC. */
function tzOffset(at: number, timeZone: string): number {
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(at));
  const v = (t: string) => Number(p.find((x) => x.type === t)!.value);
  return Date.UTC(v('year'), v('month') - 1, v('day'), v('hour'), v('minute'), v('second')) - Math.floor(at / 1000) * 1000;
}

/** Instant of local midnight starting `date` in `timeZone` (two passes so DST changes land right). */
export function localMidnight(date: string, timeZone: string): Date {
  const wall = parseDate(date);
  let t = wall - tzOffset(wall, timeZone);
  t = wall - tzOffset(t, timeZone);
  return new Date(t);
}

// ------------------------------------------------------------------ resolution
const PRESET_LABEL: Record<PeriodPreset, string> = {
  today: 'Hoy',
  yesterday: 'Ayer',
  last7: 'Últimos 7 días',
  last30: 'Últimos 30 días',
  this_month: 'Este mes',
  last_month: 'Mes anterior',
  this_year: 'Este año',
  custom: 'Rango personalizado',
};

/** Default bucket size for a range length (keeps charts between ~7 and ~60 points). */
export function defaultGranularity(days: number): Granularity {
  if (days <= 2) return 'hour';
  if (days <= 62) return 'day';
  if (days <= 190) return 'week';
  return 'month';
}

/** Bucket sizes that make sense for a range (no 9 600 hourly bars, no single monthly bar). */
export function allowedGranularities(days: number): Granularity[] {
  const out: Granularity[] = [];
  if (days <= 7) out.push('hour');
  if (days >= 2 && days <= MAX_PERIOD_DAYS) out.push('day');
  if (days >= 14) out.push('week');
  if (days >= 60) out.push('month');
  if (out.length === 0) out.push(defaultGranularity(days));
  return out;
}

function currentDays(input: PeriodInput, today: string): { from: string; to: string } {
  switch (input.preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday':
      return { from: addDays(today, -1), to: addDays(today, -1) };
    case 'last7':
      return { from: addDays(today, -6), to: today };
    case 'last30':
      return { from: addDays(today, -29), to: today };
    case 'this_month':
      return { from: monthStart(today), to: today };
    case 'last_month':
      return { from: monthStart(today, -1), to: monthEnd(today, -1) };
    case 'this_year':
      return { from: `${today.slice(0, 4)}-01-01`, to: today };
    case 'custom': {
      if (!input.from || !input.to) throw new PeriodError('El rango personalizado necesita fecha inicial y final');
      parseDate(input.from);
      parseDate(input.to);
      if (input.from > input.to) throw new PeriodError('La fecha inicial debe ser anterior o igual a la final');
      if (input.from > today) throw new PeriodError('El rango no puede empezar en el futuro');
      const to = input.to > today ? today : input.to;
      if (daysBetween(input.from, to) + 1 > MAX_PERIOD_DAYS) throw new PeriodError(`El rango máximo es de ${MAX_PERIOD_DAYS} días`);
      return { from: input.from, to };
    }
  }
}

/** Comparison days: the equivalent previous period, or the same dates one year earlier. */
function comparisonDays(preset: PeriodPreset, cur: { from: string; to: string }, mode: ComparisonMode): { from: string; to: string; label: string } | null {
  if (mode === 'none') return null;
  if (mode === 'year') return { from: shiftYears(cur.from, -1), to: shiftYears(cur.to, -1), label: 'vs. mismo período del año anterior' };
  const n = daysBetween(cur.from, cur.to) + 1;
  switch (preset) {
    case 'today':
      return { from: addDays(cur.from, -1), to: addDays(cur.to, -1), label: 'vs. ayer' };
    case 'yesterday':
      return { from: addDays(cur.from, -1), to: addDays(cur.to, -1), label: 'vs. anteayer' };
    case 'this_month': {
      // month to date vs the same days of the previous month (1–30 Sep vs 1–30 Aug; 1–31 Mar vs 1–28 Feb)
      const from = monthStart(cur.from, -1);
      const to = clampedDate(ymd(from).y, ymd(from).m, ymd(cur.to).d);
      return { from, to, label: 'vs. mismos días del mes anterior' };
    }
    case 'last_month':
      return { from: monthStart(cur.from, -1), to: monthEnd(cur.from, -1), label: 'vs. el mes previo' };
    case 'this_year':
      return { from: shiftYears(cur.from, -1), to: shiftYears(cur.to, -1), label: 'vs. mismo período del año anterior' };
    default:
      return { from: addDays(cur.from, -n), to: addDays(cur.to, -n), label: n === 1 ? 'vs. el día anterior' : `vs. los ${n} días anteriores` };
  }
}

export function resolvePeriod(input: PeriodInput, now: Date, timeZone: string): ResolvedPeriod {
  const today = localDate(now, timeZone);
  const cur = currentDays(input, today);
  const start = localMidnight(cur.from, timeZone);
  const calendarEnd = localMidnight(addDays(cur.to, 1), timeZone);
  const partial = calendarEnd.getTime() > now.getTime();
  const end = partial ? now : calendarEnd;
  const days = daysBetween(cur.from, cur.to) + 1;

  const compare = input.compare ?? 'previous';
  const cmp = comparisonDays(input.preset, cur, compare);
  let previous: PeriodWindow | null = null;
  if (cmp) {
    const pStart = localMidnight(cmp.from, timeZone);
    const pCalendarEnd = localMidnight(addDays(cmp.to, 1), timeZone);
    // unfinished period: compare the same elapsed time, capped at the end of the comparison days
    const pEnd = partial ? new Date(Math.min(pStart.getTime() + (end.getTime() - start.getTime()), pCalendarEnd.getTime())) : pCalendarEnd;
    previous = { from: cmp.from, to: cmp.to, start: pStart.toISOString(), end: pEnd.toISOString() };
  }

  const allowed = allowedGranularities(days);
  const granularity = input.granularity && allowed.includes(input.granularity) ? input.granularity : defaultGranularity(days);
  const sameTime = partial && previous ? ' (hasta la misma hora)' : '';

  return {
    preset: input.preset,
    label: input.preset === 'custom' ? `${cur.from} a ${cur.to}` : PRESET_LABEL[input.preset],
    current: { from: cur.from, to: cur.to, start: start.toISOString(), end: end.toISOString() },
    previous,
    compare,
    compareLabel: cmp ? `${cmp.label}${sameTime}` : null,
    partial,
    days,
    granularity,
    timezone: timeZone,
  };
}
